/*
 * Assistant tools for wiki review markup (AQUASTAR-606; contract: Nexus
 * Docs/design/WIKI_MARKUP_AND_EDITING_API.md §9).
 *
 * Five tools map 1:1 onto the gateway's wiki markup API and run with the
 * signed-in person's token plus `X-Nexus-Agent: assistant`, so history and
 * issues record "agent:assistant" with the person who asked as requested_by:
 *
 *   list_review_markups        GET  /wiki/markups?status=open[&page=]
 *   get_markup                 GET  /wiki/markups/:ref   (+ the snapshot, shown to the person)
 *   draft_issues_from_markup   GET  /wiki/markups/:ref/versions/:v/issues/draft
 *   create_issues_from_markup  POST /wiki/markups/:ref/versions/:v/issues     — only after the person confirms
 *   complete_markup_version    POST /wiki/markups/:ref/versions/:v/complete   — only after the person confirms
 *
 * The model gets compact digests: refs, statuses, captions, shape kinds,
 * rough positions, notes and texts. Picture URLs never reach it (File Service
 * URLs carry access tokens). The console's chat transport is text-only, so
 * the snapshot image is shown to the person on the card, not sent to the model.
 */

import { useEffect, useState } from 'react';
import { WIKI_CHANGED_EVENT, onConsoleChange } from '@nexus/admin-ui';
import { appRest } from './apiClient';
import { APP_ID, GATEWAY_BASE } from './config';

export const AGENT_HEADERS = { 'X-Nexus-Agent': 'assistant' } as const;

export interface WikiMeLite {
  role: string | null;
  staff: boolean;
  can: { read: boolean; review: boolean; edit: boolean; moderate: boolean };
}

export interface MkActor {
  name: string;
  kind: string;
}
export interface MkTarget {
  asset_sha256: string;
  view_key?: string | null;
  caption?: string | null;
}
export interface MkShape {
  shape_id: string;
  kind: string;
  geom: Record<string, unknown>;
  text?: string;
  note?: string;
}
export interface MkIssue {
  issueId: string;
  key: string;
  title: string;
  statusKey: string | null;
  statusCategory?: string | null;
  shapeIds?: string[];
  idempotent?: boolean;
}
export interface MkSummary {
  ref: string;
  page: { slug: string; title: string };
  target: MkTarget;
  status: string;
  latestVersion: number;
  versionCount: number;
  openIssueCount: number;
  issueCount: number;
  author: MkActor;
  updatedAt: string;
}
export interface MkVersion {
  ref: string;
  version: number;
  status: string;
  target: MkTarget;
  shapes: MkShape[];
  summary: string | null;
  author: MkActor;
  submittedAt: string | null;
  snapshotUrl: string | null;
  issues: MkIssue[];
}
export interface MkDetail {
  markup: MkSummary;
  versions: MkVersion[];
}
export interface MkProposal {
  title: string;
  typeKey: string;
  severityKey: string | null;
  shapeIds: string[];
  description: string;
}

export type WikiToolCall =
  | {
      tool: 'list_review_markups';
      status: 'open' | 'submitted' | 'issues_filed' | 'complete' | 'all';
      page?: string;
    }
  | { tool: 'get_markup'; ref: string; version?: number }
  | { tool: 'draft_issues_from_markup'; ref: string; version: number }
  | {
      tool: 'create_issues_from_markup';
      ref: string;
      version: number;
      issues: Array<{
        title: string;
        shapeIds: string[];
        typeKey?: string;
        severityKey?: string;
        description?: string;
      }>;
    }
  | { tool: 'complete_markup_version'; ref: string; version: number; note?: string };

export const WIKI_TOOL_NAMES = [
  'list_review_markups',
  'get_markup',
  'draft_issues_from_markup',
  'create_issues_from_markup',
  'complete_markup_version',
] as const;
const REF_RE = /^(MK-)?\d{1,9}$/i;
const SLUG_RE = /^[a-z0-9][a-z0-9/_.-]{0,200}$/i;
const SHAPE_RE = /^[A-Za-z0-9_-]{1,64}$/;
const LIST_STATUSES = new Set(['open', 'submitted', 'issues_filed', 'complete', 'all']);

const normRef = (r: unknown): string | null => {
  const s = String(r ?? '').trim();
  if (!REF_RE.test(s)) return null;
  return /^MK-/i.test(s) ? `MK-${s.slice(3)}` : `MK-${s}`;
};
const posInt = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 && n < 100_000 ? n : undefined;
};
const shortStr = (v: unknown, max: number): string =>
  String(v ?? '')
    .trim()
    .slice(0, max);

/** Parse a wiki tool call from the model's parsed JSON; null when it is not one (or not valid). */
export function parseWikiToolCall(
  name: unknown,
  args: Record<string, unknown>,
): WikiToolCall | null {
  switch (name) {
    case 'list_review_markups': {
      const st = shortStr(args.status, 20) || 'open';
      const page = shortStr(args.page, 200);
      return {
        tool: 'list_review_markups',
        status: (LIST_STATUSES.has(st) ? st : 'open') as 'open',
        ...(page && SLUG_RE.test(page) ? { page } : {}),
      };
    }
    case 'get_markup': {
      const ref = normRef(args.ref);
      if (!ref) return null;
      const version = posInt(args.version);
      return { tool: 'get_markup', ref, ...(version ? { version } : {}) };
    }
    case 'draft_issues_from_markup': {
      const ref = normRef(args.ref),
        version = posInt(args.version);
      return ref && version ? { tool: 'draft_issues_from_markup', ref, version } : null;
    }
    case 'create_issues_from_markup': {
      const ref = normRef(args.ref),
        version = posInt(args.version);
      if (!ref || !version || !Array.isArray(args.issues)) return null;
      const issues = (args.issues as Array<Record<string, unknown>>).slice(0, 20).flatMap((i) => {
        if (!i || typeof i !== 'object') return [];
        const title = shortStr(i.title, 140);
        if (!title) return [];
        const shapeIds = Array.isArray(i.shapeIds ?? i.shape_ids)
          ? ((i.shapeIds ?? i.shape_ids) as unknown[])
              .map(String)
              .filter((x) => SHAPE_RE.test(x))
              .slice(0, 200)
          : [];
        const typeKey = shortStr(i.typeKey, 40),
          severityKey = shortStr(i.severityKey, 40);
        return [
          {
            title,
            shapeIds,
            ...(typeKey ? { typeKey } : {}),
            ...(severityKey ? { severityKey } : {}),
            description: shortStr(i.description, 4000),
          },
        ];
      });
      return issues.length ? { tool: 'create_issues_from_markup', ref, version, issues } : null;
    }
    case 'complete_markup_version': {
      const ref = normRef(args.ref),
        version = posInt(args.version);
      const note = shortStr(args.note, 1000);
      return ref && version
        ? { tool: 'complete_markup_version', ref, version, ...(note ? { note } : {}) }
        : null;
    }
    default:
      return null;
  }
}

export function isWikiWrite(
  c: WikiToolCall,
): c is Extract<WikiToolCall, { tool: 'create_issues_from_markup' | 'complete_markup_version' }> {
  return c.tool === 'create_issues_from_markup' || c.tool === 'complete_markup_version';
}

// ── gateway calls ─────────────────────────────────────────────────────

/** The caller's wiki role; null when they have none or the gateway has no markup API. */
export async function fetchWikiMe(): Promise<WikiMeLite | null> {
  try {
    const r = await appRest<WikiMeLite>('GET', '/wiki/me');
    return r && r.can && typeof r.can.read === 'boolean' ? r : null;
  } catch {
    return null;
  }
}

const enc = encodeURIComponent;
export const listMarkups = (status: string, page?: string) =>
  appRest<{ items: MkSummary[]; total: number }>(
    'GET',
    `/wiki/markups?status=${enc(status)}${page ? `&page=${enc(page)}` : ''}&limit=50`,
    undefined,
    AGENT_HEADERS,
  );
export const getMarkup = (ref: string) =>
  appRest<MkDetail>('GET', `/wiki/markups/${enc(ref)}`, undefined, AGENT_HEADERS);
export const draftIssues = (ref: string, v: number) =>
  appRest<{ proposals: MkProposal[] }>(
    'GET',
    `/wiki/markups/${enc(ref)}/versions/${v}/issues/draft`,
    undefined,
    AGENT_HEADERS,
  );
export const createIssues = (
  ref: string,
  v: number,
  issues: Array<{
    title: string;
    shapeIds: string[];
    typeKey?: string;
    severityKey?: string;
    description?: string;
  }>,
) =>
  appRest<{ created: MkIssue[]; attached: MkIssue[]; version: MkVersion }>(
    'POST',
    `/wiki/markups/${enc(ref)}/versions/${v}/issues`,
    { issues },
    AGENT_HEADERS,
  );
export const completeVersion = (ref: string, v: number, note?: string) =>
  appRest<{ version: MkVersion }>(
    'POST',
    `/wiki/markups/${enc(ref)}/versions/${v}/complete`,
    { note },
    AGENT_HEADERS,
  );

/** Absolute URL for a gateway path (snapshot); fetched with the bearer token by the card. */
export const gatewayUrl = (path: string): string =>
  /^https?:/.test(path) ? path : `${GATEWAY_BASE}${path.startsWith('/') ? path : `/${path}`}`;
/** The console's wiki page for a markup (the wiki is mounted at /wiki in this console). */
export const markupLink = (slug: string, ref: string, v?: number): string =>
  `/wiki/${slug}?markup=${enc(ref)}${v ? `&v=${v}` : ''}`;
export const WIKI_APP = APP_ID;

// ── digests for the model (no URLs, no hashes) ────────────────────────

/** Rough position of a shape on the picture, in words + percent box, so the model can talk about it. */
export function where(s: MkShape): string {
  const g = s.geom as Record<string, number | number[][]>;
  let x: number, y: number;
  if (s.kind === 'pen' && Array.isArray(g.points) && g.points.length) {
    const pts = g.points as number[][];
    x = pts.reduce((a, p) => a + (p[0] ?? 0), 0) / pts.length;
    y = pts.reduce((a, p) => a + (p[1] ?? 0), 0) / pts.length;
  } else if (s.kind === 'arrow') {
    x = Number(g.x2);
    y = Number(g.y2);
  } else {
    x = Number(g.x) + (Number(g.w) || 0) / 2;
    y = Number(g.y) + (Number(g.h) || 0) / 2;
  }
  if (!Number.isFinite(x) || !Number.isFinite(y)) return 'on the picture';
  const v = y < 0.33 ? 'top' : y > 0.66 ? 'bottom' : 'middle';
  const h = x < 0.33 ? 'left' : x > 0.66 ? 'right' : 'centre';
  return `${v} ${h} (${Math.round(x * 100)}% across, ${Math.round(y * 100)}% down)`;
}

export function digestList(r: { items: MkSummary[]; total: number }) {
  return {
    total: r.total,
    markups: r.items.map((m) => ({
      ref: m.ref,
      page: m.page.slug,
      pageTitle: m.page.title,
      picture: m.target.caption || m.target.view_key || null,
      status: m.status,
      latestVersion: m.latestVersion,
      versions: m.versionCount,
      openIssues: m.openIssueCount,
      issues: m.issueCount,
      author: m.author.name,
    })),
  };
}

export function pickVersion(d: MkDetail, v?: number): MkVersion | undefined {
  const submitted = d.versions.filter((x) => x.status !== 'draft');
  return v
    ? d.versions.find((x) => x.version === v)
    : (submitted[submitted.length - 1] ?? d.versions[d.versions.length - 1]);
}

export function digestMarkup(d: MkDetail, v?: number) {
  const ver = pickVersion(d, v);
  if (!ver) return { ref: d.markup.ref, error: 'no visible version' };
  return {
    ref: d.markup.ref,
    version: ver.version,
    status: ver.status,
    versions: d.versions.filter((x) => x.status !== 'draft').map((x) => x.version),
    page: d.markup.page.slug,
    picture: ver.target.caption || ver.target.view_key || null,
    author: ver.author.name,
    summary: ver.summary,
    shapes: ver.shapes.map((s) => ({
      id: s.shape_id,
      kind: s.kind,
      where: where(s),
      ...(s.text ? { text: s.text } : {}),
      ...(s.note ? { note: s.note } : {}),
    })),
    issues: ver.issues.map((i) => ({
      key: i.key,
      title: i.title,
      status: i.statusKey,
      shapes: i.shapeIds ?? [],
    })),
    image:
      'The snapshot is shown to the user; you cannot see it — rely on the notes, texts and positions.',
  };
}

export function digestProposals(ref: string, version: number, r: { proposals: MkProposal[] }) {
  return {
    ref,
    version,
    proposals: r.proposals.map((p) => ({
      title: p.title,
      typeKey: p.typeKey,
      severityKey: p.severityKey,
      shapeIds: p.shapeIds,
    })),
  };
}

// ── system prompt section ─────────────────────────────────────────────

export function wikiToolsPrompt(me: WikiMeLite): string[] {
  const lines = [
    '',
    'You can also read review markups on wiki pages: notes and shapes reviewers drew on screenshots (refs like MK-42, versions v1, v2…). Reply with ONLY one fenced block:',
    '```tool_call',
    '{"tool":"list_review_markups","args":{"status":"open"}}',
    '```',
    '  Optional "page": a wiki page slug. Other read tools: {"tool":"get_markup","args":{"ref":"MK-42"}} (shapes, notes, linked issues; add "version" for an older one) and {"tool":"draft_issues_from_markup","args":{"ref":"MK-42","version":3}} (suggested issues, nothing is filed).',
  ];
  if (me.can.review) {
    lines.push(
      '- To file issues from a markup version, PROPOSE them; the user confirms on a card before anything is created. Group shapes that describe the same problem into one issue. Reply with ONLY:',
      '```tool_call',
      '{"tool":"create_issues_from_markup","args":{"ref":"MK-42","version":3,"issues":[{"title":"Login button sits below the fold","shapeIds":["s-1","s-3"]}]}}',
      '```',
      '- When the user says a markup version is done, propose {"tool":"complete_markup_version","args":{"ref":"MK-42","version":3,"note":"why"}}; it is also confirmed on a card.',
    );
  }
  lines.push(
    '- Always name markups by ref and version (MK-42 v3). Never invent shapes or notes; use only what a tool_result contains.',
  );
  return lines;
}

// ── open markups (the Wiki nav badge) ─────────────────────────────────

const OPEN_MARKUPS_POLL_MS = 60_000;
/** The list the badge counts and the chooser groups (open = submitted + issues filed). */
export const OPEN_MARKUPS_PATH = '/wiki/markups?status=open&limit=200';

/**
 * Number of open markups in the AquaStar wiki (item 9, AQUASTAR-599 round 2) — the same
 * for every member with wiki access, unlike the bell, which is per person. Refreshes on
 * a wiki change anywhere in the console (or another tab), when the tab comes back into
 * view, and every 60 s. 0 when disabled, without wiki access or on errors.
 */
export function useOpenMarkupCount(enabled: boolean): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) {
      setN(0);
      return;
    }
    let alive = true;
    const load = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      appRest<{ items?: unknown[]; total?: number }>('GET', OPEN_MARKUPS_PATH)
        .then((r) => alive && setN(typeof r?.total === 'number' ? r.total : Array.isArray(r?.items) ? r.items.length : 0))
        .catch(() => alive && setN(0));
    };
    load();
    const t = setInterval(load, OPEN_MARKUPS_POLL_MS);
    const off = onConsoleChange(WIKI_CHANGED_EVENT, load);
    return () => {
      alive = false;
      clearInterval(t);
      off();
    };
  }, [enabled]);
  return n;
}
